import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { StandardRiskmapService } from '../../service/standard-riskmap.service';
import { StandardRiskmapActions } from './standard-riskmap.actions';
import { HandleStandardRiskmapMutation } from '../../../../graphql/generated';
import { MessageService } from 'primeng/api';


@Injectable()
export class StandardRiskmapEffects {

  loadRiskMapsFull$ = createEffect(
    (rs: StandardRiskmapService = inject(StandardRiskmapService)) => {
      return this.actions$.pipe(

        ofType(StandardRiskmapActions.loadRiskMaps),
        mergeMap(({ parameters, size, typeOfBusines, valueChain, year, au }) =>
          rs.getStandardRiskMap(size, typeOfBusines, valueChain, year, parameters, au).pipe(
            map((riskMap) => {
              return StandardRiskmapActions.loadRiskMapsSuccess({
                riskMap,
                filtering: parameters.filtering
              });
            }),
            catchError((error) => of(StandardRiskmapActions.loadRiskMapsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllRiskMapsFull$ = createEffect(
    (rs: StandardRiskmapService = inject(StandardRiskmapService)) => {
      return this.actions$.pipe(

        ofType(StandardRiskmapActions.loadRiskMapNames),
        mergeMap(({ parameters, size, typeOfBusines, valueChain, year, au }) =>
          rs.getStandardRiskMap(size, typeOfBusines, valueChain, year, parameters, au).pipe(
            map((riskMap) => {
              return StandardRiskmapActions.loadRiskMapNamesSuccess({
                riskMap,
                filtering: parameters.filtering
              });
            }),
            catchError((error) => of(StandardRiskmapActions.loadRiskMapNamesFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  handleRiskMapTree$ = createEffect(
    (rs: StandardRiskmapService = inject(StandardRiskmapService)) => {
      return this.actions$.pipe(

        ofType(StandardRiskmapActions.handleRiskMap),
        mergeMap(({ cptIds, parameterName, processId, riskId, size, typeOfBusines, value, valueChain, year, filter }) =>
          rs.handleStandardRiskMapTree(valueChain, typeOfBusines, size, year, processId, riskId, value, filter).pipe(
            map((riskMap) => {
              return StandardRiskmapActions.handleRiskMapSuccess({
                riskMap: riskMap as HandleStandardRiskmapMutation['handleRiskMapTemplate'],
                cptIds,
                parameterName
              });
            }),
            catchError((error) => of(StandardRiskmapActions.handleRiskMapFailure({ error, cptIds, parameterName })))
          )
        )
      );
    },
    { functional: true }
  );

  updateStandardRiskMapAu$ = createEffect(
    (rs: StandardRiskmapService = inject(StandardRiskmapService)) => {
      return this.actions$.pipe(

        ofType(StandardRiskmapActions.updateStandardRiskMapAu),
        mergeMap(({ cptIds, parameterName, processId, size, typeOfBusines, valueChain, year }) =>
          rs.updateAuStandardRiskMap(processId, size, typeOfBusines, valueChain, year)
            .pipe(
              map((riskMap) => {
                return StandardRiskmapActions.updateStandardRiskMapAuSuccess({
                  riskMap: riskMap,
                  cptIds,
                  parameterName
                });
              }),
              catchError((error) => of(StandardRiskmapActions.updateStandardRiskMapAuFailure({ error, cptIds, parameterName })))
            )
        )
      );
    },
    { functional: true }
  );

  updateStandardRiskMapStatus$ = createEffect(
    (rs: StandardRiskmapService = inject(StandardRiskmapService)) => {
      return this.actions$.pipe(

        ofType(StandardRiskmapActions.updateStandardRiskMapStatus),
        mergeMap(({ cptIds, parameterName, processId, size, status, typeOfBusines, valueChain, year }) =>
          rs.updateStatusStandardRiskMap(processId, size, status, typeOfBusines, valueChain, year)
            .pipe(
              map((riskMap) => {
                return StandardRiskmapActions.updateStandardRiskMapStatusSuccess({
                  riskMap: riskMap,
                  cptIds,
                  parameterName
                });
              }),
              catchError((error) => of(StandardRiskmapActions.updateStandardRiskMapStatusFailure({ error, cptIds, parameterName })))
            )
        )
      );
    },
    { functional: true }
  );

  uploadExcel$ = createEffect(
    (rs: StandardRiskmapService = inject(StandardRiskmapService), messageService: MessageService = inject(MessageService)) => {
      return this.actions$.pipe(

        ofType(StandardRiskmapActions.uploadExcel),
        mergeMap(({ base64ExcelFile, size, typeOfBusines, valueChain, year, parameters, au }) =>
          rs.uploadExcel(base64ExcelFile)
            .pipe(
              map((res) => {

                messageService.add({ summary: res!.type!, detail: res!.message! + res?.status, severity: res?.status ? "success" : "error", life: 86400000 })
                if (size && typeOfBusines && valueChain && year)
                  return StandardRiskmapActions.loadRiskMaps({
                    parameters, size, typeOfBusines, valueChain, year: String(year), au
                  });
                else
                  return StandardRiskmapActions.uploadExcelSuccess({
                    res: res
                  })
              }),
              catchError((error) => of(StandardRiskmapActions.uploadExcelFailure({ error })))
            )
        )
      );
    },
    { functional: true }
  );

  getFilters$ = createEffect(
    (rs: StandardRiskmapService = inject(StandardRiskmapService)) => {
      return this.actions$.pipe(

        ofType(StandardRiskmapActions.loadAllRiskMapTree),
        mergeMap(({ year, valueChain, typeOfBusiness, size, au }) =>
          rs.getFilters(year, valueChain, typeOfBusiness, size, au)
            .pipe(
              map((filters) => {
                return StandardRiskmapActions.loadAllRiskMapTreeSuccess({
                  filters
                });
              }),
              catchError((error) => of(StandardRiskmapActions.loadAllRiskMapTreeFailure({ error })))
            )
        )
      );
    },
    { functional: true }
  );


  setParameters$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StandardRiskmapActions.setParameters)
        // Qui potresti aggiungere altre operazioni se necessario
      ),
    { functional: true, dispatch: false }
  );

  clearRiskMap = createEffect(() =>
    this.actions$.pipe(
      ofType(StandardRiskmapActions.clearRiskMaps),
      map(() => StandardRiskmapActions.clearRiskMapState()) // Dispatcha un'azione per ripulire lo stato
    )
  );

  downloadStandardRiskmap$ = createEffect(() => {
    const as = inject(StandardRiskmapService);
    return this.actions$.pipe(
      ofType(StandardRiskmapActions.downloadStandardRiskmap),
      mergeMap(({ parameters, timeZone, year, size, typeOfBusiness, valueChain }) => {
        return as.downloadStandardRiskMap(parameters, timeZone, Number(year), valueChain, typeOfBusiness, size).pipe(
          map((res) => {
            if (res.length > 0) {
              this.downloadFile(res[0].content, res[0].filename);
            } else {
              console.error("Dati non validi:", res);
            }
            return StandardRiskmapActions.downloadStandardRiskmapSuccess({
              export: res
            });
          }),
          catchError((error) => {
            console.error("Errore durante il download:", error);
            return of(StandardRiskmapActions.downloadStandardRiskMapFailure({ error }));
          })
        );
      })
    );
  });


  async downloadFile(response: string, fileName: string | undefined) {
    const typeFormat = {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    };
    const link = window.document.createElement("a");
    const blob = this.base64ToBlob(response, typeFormat.type);
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName ?? "fileExport";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(link.href);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }


  constructor(private actions$: Actions) { }
}

